import { Fragment, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOrgScopeKey } from '@/lib/org-scope/react';

import type { RouteNode } from './config';

/**
 * A protected route's page, scoped to the active organization the way the route
 * config says: mounted afresh on every org switch (`meta.orgScope`, default
 * `'reset'`), or left alone for a page marked `'keep'`.
 *
 * The one place that decision is made. App.tsx applies it to every protected
 * route, and the org-switch QA harness (`src/dev/orgSwitchQa`) mounts pages
 * through it, so the browser test exercises the app's own wiring.
 */
export function withOrgScope(meta: RouteNode['meta'], page: ReactElement): ReactElement {
  if (meta?.orgScope === 'keep') return page;
  return <OrgScopedRoute params={meta?.orgScopedParams ?? NO_PARAMS}>{page}</OrgScopedRoute>;
}

const NO_PARAMS: readonly string[] = [];

/**
 * Remounts `children` on every org switch. Before the fresh page renders, it drops
 * the route's `orgScopedParams` from the URL: those name one org's records (a Weave
 * `?session=`, an agent's `?run_id=`), and the fresh page would read them as its own.
 * The page renders nothing for the one frame the URL takes to settle.
 */
function OrgScopedRoute({ params, children }: { params: readonly string[]; children: ReactNode }) {
  const key = useOrgScopeKey();
  const [searchParams, setSearchParams] = useSearchParams();
  // The key whose URL has been checked. Behind `key` only between a switch and its effect.
  const [settledKey, setSettledKey] = useState(key);
  const switching = settledKey !== key;
  const stale = switching && params.some((p) => searchParams.has(p));

  useEffect(() => {
    if (!switching) return;
    if (stale) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const p of params) next.delete(p);
          return next;
        },
        { replace: true },
      );
    }
    setSettledKey(key);
  }, [switching, stale, key, params, setSearchParams]);

  if (stale) return null;
  return <Fragment key={key}>{children}</Fragment>;
}
